import {
	Box,
	Button,
	Checkbox,
	Chip,
	Divider,
	FormControl,
	Grid,
	Icon,
	IconButton,
	InputLabel,
	ListItemText,
	MenuItem,
	Modal,
	Paper,
	Select,
	SelectChangeEvent,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import * as yup from "yup";
import { ToastContent } from "../../../../shared/components";
import { IVFormErros, useVForm, VForm, VTextField } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { useToast } from "../../../../shared/hooks";
import { ICreateSinisterValues, ISinisterValues, SinisterService } from "../../../../shared/services/api/sinister/SinisterService";
import { SinisterDocumentsService } from "../../../../shared/services/api/sinister/SinisterDocumentsService";
import { globalFormatCurrencyBRL, globalRemoveMask } from "../../../../shared/utils";
import { ModalPreviewValue } from "./ModalPreviewValue";

interface Props {
	open: boolean;
	onClose: (updateData: boolean) => void; // Passando uma prop para o componente pai
	sinisterId: string;
	onSuccess?: () => void;
	// Duplicar/espelhar um lançamento existente (item 7) — pré-preenche a primeira linha com os
	// valores desse débito, mas continua tudo editável antes de confirmar.
	duplicateFrom?: ISinisterValues;
}

interface IFormData {
	sinisterId: string;
	status: string;
	batchReason: string;
}

type TValueRow = {
	id: string;
};

type TValueRowDraft = {
	typeId?: string;
	reference?: string;
	dueDate?: string;
	value?: string;
};

const SINISTER_VALUE_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
	{ value: "rent_values", label: "Aluguel" },
	{ value: "water_values", label: "Água" },
	{ value: "fire_insurance", label: "Apólice seguro incêndio" },
	{ value: "condominium_values", label: "Condomínio" },
	{ value: "damage", label: "Danos ao Imóvel" },
	{ value: "energy_values", label: "Energia Elétrica" },
	{ value: "iptu", label: "IPTU" },
	{ value: "gas_values", label: "Gás" },
	{ value: "cleaning", label: "Limpeza" },
	{ value: "fine_on_rent", label: "Multa sobre Aluguel" },
	{ value: "fine_on_condominium", label: "Multa sobre valor do condomínio" },
	{ value: "painting", label: "Pintura" },
	{ value: "garantti_tax", label: "Taxa mensal Garantti" },
	{ value: "other", label: "Outros" },
];

const allowedFileSize = 50 * 1024 * 1024;
const allowedMimetypes = [
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.oasis.opendocument.text",
	"image/jpeg",
	"image/jpg",
	"image/png",
];

const validateAttachment = (file: File): string[] => {
	const errors: string[] = [];
	if (file.size > allowedFileSize) {
		errors.push(`Tamanho do arquivo excede ${(allowedFileSize / (1024 * 1024)).toFixed(0)}MB`);
	}
	if (!allowedMimetypes.includes(file.type)) {
		errors.push("Tipo de arquivo inválido");
	}
	return errors;
};

const createRowId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

// Upload em massa por arquivo (itens 10/10b/11, GARV2-1463/1464).
// rowIds identifica a qual(is) linha(s)/cobrança(s) este anexo pertence — bug real encontrado em
// produção (2026-09-25): com mais de uma linha, todo anexo era enviado pra TODAS as linhas (o
// mesmo boleto de aluguel acabava vinculado também ao débito de água, por exemplo). Com 1 linha
// só, o anexo é atribuído automaticamente a ela; com 2+, o usuário escolhe explicitamente — e pode
// escolher mais de uma linha quando um único comprovante cobre várias cobranças (ex.: boleto
// combinado de aluguel + condomínio). Sem seletor de "tipo de documento" — o backend não tem
// campo pra isso hoje em nenhum dos endpoints usados aqui (ver GAPS_BORDERO_PARA_BACKEND.md).
type TAttachment = {
	id: string;
	file: File;
	rowIds: string[];
};

const formatFileSize = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const ModalAddValues = ({ open, onClose, sinisterId, onSuccess, duplicateFrom }: Props) => {
	const { formRef } = useVForm();
	const [isLoading, setIsLoading] = useState(false);
	const [attachments, setAttachments] = useState<TAttachment[]>([]);
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const [isDuplicatingAttachments, setIsDuplicatingAttachments] = useState(false);
	const [previewAttachment, setPreviewAttachment] = useState<TAttachment | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const toastHandler = useToast();
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const ReferenceRegex = /^(0[1-9]|1[0-2])\/\d{4}$/;
	const [rows, setRows] = useState<TValueRow[]>([{ id: createRowId() }]);
	const [draftByRowId, setDraftByRowId] = useState<Record<string, TValueRowDraft>>({});

	const rowIds = useMemo(() => rows.map((r) => r.id), [rows]);

	// Pré-preenche a primeira linha com os dados do lançamento sendo duplicado (item 7). O
	// VTextField/VMaskTextField só registram seu setValue no unform num useEffect próprio, que só
	// roda DEPOIS do primeiro paint — chamar setFieldValue antes disso (mesmo dentro de um
	// useEffect do pai) faz o valor ficar só na cópia local (draftByRowId, daí o Resumo aparecer
	// certo) sem nunca chegar no campo/no dado real do formulário. O setTimeout(0) empurra a
	// chamada pra depois desse registro.
	useEffect(() => {
		if (!open || !duplicateFrom) return;

		const firstRowId = rows[0]?.id;
		if (!firstRowId) return;

		const valueFormatted = Number(duplicateFrom.value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		const dueDateFormatted = duplicateFrom.dueDate ? format(new Date(duplicateFrom.dueDate), "yyyy-MM-dd") : "";

		const prefillTimer = setTimeout(() => {
			formRef.current?.setFieldValue(`typeId_${firstRowId}`, duplicateFrom.typeId ?? "");
			formRef.current?.setFieldValue(`reference_${firstRowId}`, duplicateFrom.reference ?? "");
			formRef.current?.setFieldValue(`dueDate_${firstRowId}`, dueDateFormatted);
			formRef.current?.setFieldValue(`value_${firstRowId}`, valueFormatted);
		}, 0);

		setDraftByRowId((prev) => ({
			...prev,
			[firstRowId]: {
				typeId: duplicateFrom.typeId ?? "",
				reference: duplicateFrom.reference ?? "",
				dueDate: dueDateFormatted,
				value: valueFormatted,
			},
		}));

		// É pra ser uma cópia de verdade: também duplica o(s) comprovante(s) do lançamento
		// original — baixa o arquivo e recoloca como anexo pendente, já vinculado a esta linha. O
		// usuário pode trocar ou remover antes de confirmar. Substitui (não soma) o estado de
		// anexos — evita acumular sobras de uma duplicação anterior nesta mesma instância do modal
		// (ex.: duplicar, cancelar, duplicar outra linha).
		// dedupe por id — se a listagem do backend vier com o mesmo documento repetido, não
		// queremos duplicar o anexo duplicado.
		const originalDocs = Array.from(new Map((duplicateFrom.sinisterValuesDocuments ?? []).map((doc) => [doc.id, doc])).values());
		if (originalDocs.length === 0) {
			setAttachments([]);
			return () => clearTimeout(prefillTimer);
		}

		let cancelled = false;
		setIsDuplicatingAttachments(true);
		Promise.all(
			originalDocs.map(async (doc) => {
				const result = await SinisterDocumentsService.previewDocumentBySinisterValues(doc.id);
				if (result instanceof Error) return null;
				const fileName = doc.originalFilename || doc.filename || `documento-${doc.id}`;
				const file = new File([result], fileName, { type: result.type || doc.mimetype || "application/octet-stream" });
				return { id: createRowId(), file, rowIds: [firstRowId] } as TAttachment;
			})
		)
			.then((duplicatedAttachments) => {
				if (cancelled) return;
				const valid = duplicatedAttachments.filter((a): a is TAttachment => a !== null);
				setAttachments(valid);
				if (valid.length < originalDocs.length) {
					toastHandler.present({
						type: "error",
						position: "top-center",
						messageOrContent: "Não foi possível copiar um ou mais anexos do lançamento original.",
					});
				}
			})
			.finally(() => {
				if (!cancelled) setIsDuplicatingAttachments(false);
			});

		return () => {
			cancelled = true;
			clearTimeout(prefillTimer);
		};
	}, [open, duplicateFrom]);

	const handlePickAttachmentClick = () => {
		fileInputRef.current?.click();
	};

	const handleAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selected = Array.from(event.target.files ?? []);
		if (selected.length === 0) return;

		// Com 1 linha só, não tem ambiguidade — atribui direto. Com 2+, fica sem linha até o
		// usuário escolher explicitamente (ver seletor "Cobrança" abaixo do arquivo) — pode
		// escolher mais de uma, quando o mesmo comprovante cobre várias cobranças.
		const defaultRowIds = rows.length === 1 ? [rows[0].id] : [];

		const valid: TAttachment[] = [];
		selected.forEach((selectedFile) => {
			const errors = validateAttachment(selectedFile);
			if (errors.length > 0) {
				errors.forEach((msg) =>
					toastHandler.present({ type: "error", position: "top-center", messageOrContent: `${selectedFile.name}: ${msg}` })
				);
				return;
			}
			valid.push({ id: createRowId(), file: selectedFile, rowIds: defaultRowIds });
		});

		setAttachments((prev) => [...prev, ...valid]);
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const handleRemoveAttachment = (id: string) => {
		setAttachments((prev) => prev.filter((a) => a.id !== id));
	};

	const handleChangeAttachmentRows = (id: string, newRowIds: string[]) => {
		setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, rowIds: newRowIds } : a)));
	};

	// Preview do arquivo antes de excluir — como ainda não foi enviado, é só um blob local (o
	// arquivo em si), sem precisar de nenhuma chamada à API.
	const handleOpenAttachmentPreview = (attachment: TAttachment) => {
		if (previewUrl) URL.revokeObjectURL(previewUrl);
		setPreviewUrl(URL.createObjectURL(attachment.file));
		setPreviewAttachment(attachment);
	};

	const handleCloseAttachmentPreview = () => {
		if (previewUrl) URL.revokeObjectURL(previewUrl);
		setPreviewUrl(null);
		setPreviewAttachment(null);
	};

	const addRow = () => {
		setRows((prev) => [...prev, { id: createRowId() }]);
	};

	const removeRow = (id: string) => {
		if (rows.length <= 1) return;
		const remaining = rows.filter((r) => r.id !== id);
		setRows(remaining);
		setDraftByRowId((prev) => {
			if (!prev[id]) return prev;
			const next = { ...prev };
			delete next[id];
			return next;
		});
		// Tira a linha removida de qualquer anexo vinculado a ela (evita apontar pra uma linha que
		// não existe mais). Se só sobrou 1 linha, reatribui todo anexo sem dono nenhum pra ela
		// (volta a ser o caso simples).
		const soleRemainingId = remaining.length === 1 ? remaining[0].id : null;
		setAttachments((prev) =>
			prev.map((a) => {
				const nextRowIds = a.rowIds.filter((rowId) => rowId !== id);
				if (nextRowIds.length === 0 && soleRemainingId) return { ...a, rowIds: [soleRemainingId] };
				return { ...a, rowIds: nextRowIds };
			})
		);
	};

	const effectiveDraftRows = useMemo(() => {
		return rows
			.map((row) => ({
				rowId: row.id,
				draft: draftByRowId[row.id] ?? {},
			}))
			.filter(({ draft }) => {
				return !!(draft.typeId || draft.reference || draft.dueDate || draft.value);
			});
	}, [rows, draftByRowId]);

	const launchSummary = useMemo(() => {
		const items = effectiveDraftRows.map(({ rowId, draft }, index) => {
			const typeLabel = SINISTER_VALUE_TYPE_OPTIONS.find((o) => o.value === draft.typeId)?.label ?? "";
			const normalized = globalRemoveMask(draft.value ?? "");
			const numeric = Number(normalized) || 0;
			return {
				rowId,
				index: index + 1,
				typeId: draft.typeId ?? "",
				typeLabel,
				reference: draft.reference ?? "",
				dueDate: draft.dueDate ?? "",
				valueRaw: draft.value ?? "",
				valueNumeric: numeric,
				valueFormatted: globalFormatCurrencyBRL(normalized),
			};
		});

		const total = items.reduce((acc, item) => acc + item.valueNumeric, 0);
		return {
			items,
			count: items.length,
			totalNumeric: total,
			totalFormatted: total.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
		};
	}, [effectiveDraftRows]);

	const rowValidationSchema: yup.SchemaOf<ICreateSinisterValues> = yup.object().shape({
		typeId: yup.string().required("Campo Obrigatório"),
		value: yup.string().required("Campo Obrigatório"),
		reference: yup.string().matches(ReferenceRegex, "Referência inválida. Digite no formato MM/AAAA.").required("Campo Obrigatório"),
		dueDate: yup.string().required("Campo Obrigatório"),
	});

	const handleSave = async (dados: IFormData) => {
		if (attachments.length === 0) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={"Erro"} content={"Obrigatório anexar ao menos um arquivo/comprovante"} />,
			});
			return;
		}

		// Com mais de uma linha, cada anexo precisa estar vinculado a pelo menos uma cobrança —
		// sem isso o anexo não sabe pra qual linha ir (bug real encontrado: sem essa checagem, os
		// anexos eram mandados pra TODAS as linhas).
		if (rows.length > 1) {
			const unassignedAttachment = attachments.find((a) => a.rowIds.length === 0);
			if (unassignedAttachment) {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: (
						<ToastContent title={"Erro"} content={`Selecione a cobrança de "${unassignedAttachment.file.name}"`} />
					),
				});
				return;
			}
		}

		setIsLoading(true);
		setIsUploading(true);
		formRef.current?.setErrors({});

		const formData = (formRef.current?.getData?.() ?? {}) as Record<string, any>;
		const batchReason = (formData.batchReason ?? "") as string;

		const candidates = rowIds.map((rowId) => {
			return {
				rowId,
				data: {
					typeId: (formData[`typeId_${rowId}`] ?? "") as string,
					value: (formData[`value_${rowId}`] ?? "") as string,
					reference: (formData[`reference_${rowId}`] ?? "") as string,
					dueDate: (formData[`dueDate_${rowId}`] ?? "") as string,
				},
			};
		});

		const effectiveRows = candidates.filter(({ data }) => {
			return !!(data.typeId || data.value || data.reference || data.dueDate);
		});

		if (effectiveRows.length === 0) {
			setIsLoading(false);
			setIsUploading(false);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={"Erro"} content={"Preencha ao menos uma linha para lançar"} />,
			});
			return;
		}

		const rowWithoutAttachment = effectiveRows.find((row) => !attachments.some((a) => a.rowIds.includes(row.rowId)));
		if (rowWithoutAttachment) {
			setIsLoading(false);
			setIsUploading(false);
			const typeLabel = SINISTER_VALUE_TYPE_OPTIONS.find((o) => o.value === rowWithoutAttachment.data.typeId)?.label;
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: (
					<ToastContent title={"Erro"} content={`Anexe ao menos um comprovante para a linha${typeLabel ? ` "${typeLabel}"` : ""}`} />
				),
			});
			return;
		}

		const validationErros: IVFormErros = {};
		for (const row of effectiveRows) {
			try {
				await rowValidationSchema.validate(row.data, { abortEarly: false });
			} catch (errors: any) {
				const yupError = errors as yup.ValidationError;
				yupError.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[`${error.path}_${row.rowId}`] = error.message;
				});
			}
		}

		if (Object.keys(validationErros).length > 0) {
			setIsLoading(false);
			setIsUploading(false);
			formRef.current?.setErrors(validationErros);
			return;
		}

		// Cada linha só usa os anexos vinculados a ela (attachment.rowIds) — não é mais um
		// primary/extras compartilhado entre todas as linhas (bug real corrigido em 2026-09-25: um
		// mesmo comprovante ia parar em cobranças diferentes). Um mesmo anexo pode estar vinculado a
		// mais de uma linha (comprovante combinado) — nesse caso ele é reenviado uma vez por linha.
		// O primeiro anexo da linha segue o fluxo já existente (POST /sinister-values com file
		// embutido); os demais (item 10b) são enviados um a um pelo endpoint de documentos por valor.
		const createdValueIds: string[] = [];
		try {
			for (const row of effectiveRows) {
				const [primaryAttachment, ...extraAttachments] = attachments.filter((a) => a.rowIds.includes(row.rowId));

				const payload: ISinisterValues = {
					sinisterId: sinisterId,
					status: "open",
					dueDate: format(new Date(row.data.dueDate + " 03:00:00"), "yyyy-MM-dd"),
					value: globalRemoveMask(row.data.value),
					reason: batchReason,
					reference: row.data.reference,
					typeId: row.data.typeId,
					file: primaryAttachment.file,
				};

				const result = await SinisterService.createValue(payload, primaryAttachment.file);
				const createdId = (result?.id ?? result?.data?.id) as string | undefined;
				if (typeof createdId === "string" && createdId.length > 0) {
					createdValueIds.push(createdId);

					for (const extra of extraAttachments) {
						await SinisterDocumentsService.uploadSinisterValuesDocument(sinisterId, createdId, "", extra.file);
					}
				}
			}

			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title={"Sucesso"} content={"Registros gravados com sucesso"} />,
			});
			setAttachments([]);
			if (fileInputRef.current) fileInputRef.current.value = "";
			onSuccess && onSuccess();
			onClose(true);
		} catch (e: any) {
			// best-effort rollback (sem mexer no backend): cancela o que foi criado antes do erro
			if (createdValueIds.length > 0) {
				await Promise.all(
					createdValueIds.map((id) =>
						SinisterService.updateValueCanceled(id, {
							reasonCancel: "Rollback: falha no lançamento em lote (modal planilha).",
						}),
					),
				);
			}

			toastHandler.present({
				type: "error",
				messageOrContent: e?.message ?? "Erro ao lançar valores",
				position: "top-center",
			});
		} finally {
			setIsLoading(false);
			setIsUploading(false);
		}
	};

	return (
		<>
		<Modal open={open} onClose={() => onClose(false)}>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					bgcolor: "background.paper",
					boxShadow: 24,
					width: "96%",
					maxWidth: 1024,
					maxHeight: "90vh",
					overflow: "hidden",
					borderRadius: 2,
					display: "flex",
					flexDirection: "column",
				}}
			>
				{/* Header fixo */}
				<Box
					sx={{
						px: 3,
						py: 2,
						borderBottom: "1px solid",
						borderColor: "divider",
						flexShrink: 0,
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						gap: 2,
					}}
				>
					<Box sx={{ minWidth: 0 }}>
						<Typography variant={"h5"} sx={{ fontWeight: 300 }} color={"primary.main"}>
							Adicionar valores ao sinistro
						</Typography>
						<Typography variant={"body2"} color={"text.secondary"} sx={{ mt: 0.5 }}>
							{duplicateFrom
								? "Linha e anexo(s) pré-preenchidos a partir do lançamento duplicado — confira e ajuste antes de confirmar."
								: "Insira uma ou mais linhas e anexe um único comprovante."}
						</Typography>
					</Box>
					<IconButton onClick={() => onClose(false)} sx={{ mt: -0.5, mr: -1 }}>
						<Icon>close</Icon>
					</IconButton>
				</Box>

				{/* Body rolável */}
				<Box sx={{ px: 3, py: 2, overflowY: "auto", overflowX: "hidden", flex: 1 }}>
					<VForm id="formIdSave" placeholder="" ref={formRef} onSubmit={handleSave} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
						<Grid container direction="column" padding={1} spacing={2}>
							<Grid item xs={12}>
								<TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: "auto" }}>
									<Table
										size="small"
										stickyHeader
										aria-label="Tabela de lançamentos"
										sx={{
											minWidth: 860,
											tableLayout: "fixed",
											"& .MuiTableCell-root": { px: 1, py: 1 },
										}}
									>
										<TableHead>
											<TableRow>
												<TableCell sx={{ width: 36 }}>#</TableCell>
												<TableCell sx={{ width: 320 }}>Tipo</TableCell>
												<TableCell sx={{ width: 140 }}>Competência</TableCell>
												<TableCell sx={{ width: 148 }}>Vencimento</TableCell>
												<TableCell sx={{ width: 160 }}>Valor</TableCell>
												<TableCell align="center" sx={{ width: 56 }}></TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{rows.map((row, index) => (
												<TableRow key={row.id}>
													<TableCell>
														<Typography variant="body2" color="text.secondary">
															{index + 1}
														</Typography>
													</TableCell>
													<TableCell>
														<VTextField
															fullWidth
															hiddenLabel
															label=""
															name={`typeId_${row.id}`}
															disabled={isLoading}
															select
															onChange={(e: any) => {
																const nextValue = e?.target?.value as string;
																setDraftByRowId((prev) => ({
																	...prev,
																	[row.id]: { ...prev[row.id], typeId: nextValue },
																}));
															}}
														>
															<MenuItem value={""} disabled>
																Selecione…
															</MenuItem>
															{SINISTER_VALUE_TYPE_OPTIONS.map((opt) => (
																<MenuItem key={opt.value} value={opt.value}>
																	{opt.label}
																</MenuItem>
															))}
														</VTextField>
													</TableCell>
													<TableCell>
														<VMaskTextField
															fullWidth
															hiddenLabel
															placeholder={"MM/AAAA"}
															label=""
															name={`reference_${row.id}`}
															disabled={isLoading}
															mask="mesano"
															onChange={(e: any) => {
																const nextValue = e?.target?.value as string;
																setDraftByRowId((prev) => ({
																	...prev,
																	[row.id]: { ...prev[row.id], reference: nextValue },
																}));
															}}
														/>
													</TableCell>
													<TableCell>
														<VTextField
															fullWidth
															hiddenLabel
															label=""
															name={`dueDate_${row.id}`}
															disabled={isLoading}
															type="date"
															onChange={(e: any) => {
																const nextValue = e?.target?.value as string;
																setDraftByRowId((prev) => ({
																	...prev,
																	[row.id]: { ...prev[row.id], dueDate: nextValue },
																}));
															}}
														/>
													</TableCell>
													<TableCell>
														<VMaskTextField
															fullWidth
															hiddenLabel
															placeholder="0,00"
															label=""
															name={`value_${row.id}`}
															disabled={isLoading}
															mask="real"
															onChange={(e: any) => {
																const nextValue = e?.target?.value as string;
																setDraftByRowId((prev) => ({
																	...prev,
																	[row.id]: { ...prev[row.id], value: nextValue },
																}));
															}}
														/>
													</TableCell>
													<TableCell align="center">
														<Tooltip title={rows.length <= 1 ? "Mínimo de 1 linha" : "Remover linha"}>
															<span>
																<IconButton
																	size="small"
																	onClick={() => removeRow(row.id)}
																	disabled={isLoading || rows.length <= 1}
																>
																	<Icon>delete</Icon>
																</IconButton>
															</span>
														</Tooltip>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Grid>

							<Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="body2" color="text.secondary">
									{rows.length} {rows.length === 1 ? "linha" : "linhas"}
								</Typography>
								<Button variant="outlined" onClick={addRow} disabled={isLoading} startIcon={<Icon>add</Icon>}>
									Adicionar linha
								</Button>
							</Grid>
						</Grid>

						<Grid container item direction="column" spacing={1}>
							<Grid item>
								<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1 }}>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											gap: 2,
											flexWrap: "wrap",
											mb: attachments.length > 0 ? 1.5 : 0,
										}}
									>
										<Box sx={{ minWidth: 0 }}>
											<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
												Comprovantes ({attachments.length})
											</Typography>
											<Typography variant="caption" color="text.secondary" noWrap>
												{isDuplicatingAttachments
													? "Copiando anexo(s) do lançamento original…"
													: effectiveDraftRows.length === 0
													? "Preencha ao menos uma linha antes de anexar"
													: `PDF, DOC, DOCX, ODT, PNG, JPG · até ${(allowedFileSize / (1024 * 1024)).toFixed(0)}MB cada — pode selecionar vários`}
											</Typography>
										</Box>

										<Tooltip
											title={effectiveDraftRows.length === 0 ? "Preencha ao menos uma linha antes de anexar" : ""}
										>
											<span>
												<Button
													variant="contained"
													onClick={handlePickAttachmentClick}
													disabled={isLoading || isUploading || isDuplicatingAttachments || effectiveDraftRows.length === 0}
													startIcon={<Icon>upload_file</Icon>}
												>
													Adicionar arquivo(s)
												</Button>
											</span>
										</Tooltip>
									</Box>

									{attachments.length > 0 && (
										<Stack spacing={1}>
											{attachments.map((attachment) => (
												<Box
													key={attachment.id}
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1.5,
														p: 1,
														borderRadius: 1.5,
														backgroundColor: "action.hover",
														flexWrap: "wrap",
													}}
												>
													<Icon sx={{ color: "text.secondary" }}>description</Icon>
													<Box sx={{ minWidth: 0, flex: "1 1 220px" }}>
														<Typography variant="body2" noWrap>
															{attachment.file.name}
														</Typography>
														<Typography variant="caption" color="text.secondary">
															{formatFileSize(attachment.file.size)}
														</Typography>
													</Box>

													{rows.length > 1 && (
														<FormControl size="small" sx={{ minWidth: 240, flex: "1 1 240px" }} error={attachment.rowIds.length === 0}>
															<InputLabel id={`attachment-row-${attachment.id}`}>Cobrança</InputLabel>
															<Select
																labelId={`attachment-row-${attachment.id}`}
																label="Cobrança"
																multiple
																value={attachment.rowIds}
																disabled={isLoading}
																onChange={(e: SelectChangeEvent<string[]>) =>
																	handleChangeAttachmentRows(
																		attachment.id,
																		typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value
																	)
																}
																renderValue={(selected) => (
																	<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
																		{selected.map((rowId) => {
																			const rowIndex = rows.findIndex((r) => r.id === rowId);
																			const typeLabel = SINISTER_VALUE_TYPE_OPTIONS.find(
																				(o) => o.value === draftByRowId[rowId]?.typeId
																			)?.label;
																			return (
																				<Chip
																					key={rowId}
																					size="small"
																					label={`${rowIndex + 1} — ${typeLabel ?? "sem tipo"}`}
																				/>
																			);
																		})}
																	</Box>
																)}
															>
																{rows.map((row, index) => {
																	const draft = draftByRowId[row.id];
																	const typeLabel = SINISTER_VALUE_TYPE_OPTIONS.find((o) => o.value === draft?.typeId)?.label;
																	return (
																		<MenuItem key={row.id} value={row.id}>
																			<Checkbox size="small" checked={attachment.rowIds.includes(row.id)} sx={{ p: 0.5, mr: 1 }} />
																			<ListItemText
																				primary={`${index + 1} — ${typeLabel ?? "sem tipo"}${draft?.reference ? ` (${draft.reference})` : ""}`}
																			/>
																		</MenuItem>
																	);
																})}
															</Select>
														</FormControl>
													)}

													<Tooltip title="Visualizar">
														<IconButton size="small" onClick={() => handleOpenAttachmentPreview(attachment)}>
															<Icon>visibility</Icon>
														</IconButton>
													</Tooltip>

													<IconButton size="small" onClick={() => handleRemoveAttachment(attachment.id)} disabled={isLoading || isUploading}>
														<Icon>delete</Icon>
													</IconButton>
												</Box>
											))}
										</Stack>
									)}
								</Paper>

								<input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={handleAttachmentChange} />
							</Grid>
						</Grid>

						<Grid item xs={12} sx={{ mt: 0.5, mb: 1 }}>
							<VTextField
								fullWidth
								label="Observação (aplica a todas as linhas)"
								name="batchReason"
								maxRows={3}
								multiline
								disabled={isLoading}
								placeholder="Ex.: boleto único com aluguel + água + energia"
							/>
						</Grid>

						<Grid container item direction="column" spacing={1} sx={{ mt: 0.5 }}>
							<Grid item>
								<Paper elevation={0} sx={{ py: 2, borderRadius: 2, width: "100%" }}>
									<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
										Resumo do lançamento
									</Typography>
									<Grid container spacing={2} sx={{ mt: 0.5, width: "100%", mx: 0 }}>
										<Grid item xs={12} md={8}>
											<Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: "action.hover" }}>
												<Typography variant="caption" color="text.secondary">
													Itens ({launchSummary.count})
												</Typography>
												<Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
													{launchSummary.count > 0 ? (
														launchSummary.items.map((item, i) => (
															<Box key={item.rowId}>
																<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
																	<Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
																		<Box component="span" sx={{ typography: "caption", color: "text.secondary", mr: 1 }}>
																			{item.reference || "—"}
																		</Box>
																		<Box component="span" sx={{ fontWeight: 500 }}>
																			{item.typeLabel || "—"}
																		</Box>
																	</Typography>
																	<Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0, textAlign: "right" }}>
																		{item.valueRaw ? item.valueFormatted : "—"}
																	</Typography>
																</Box>
																{i < launchSummary.items.length - 1 && <Divider sx={{ mt: 1 }} />}
															</Box>
														))
													) : (
														<Typography variant="body2" color="text.secondary">
															Preencha as linhas acima para ver o resumo.
														</Typography>
													)}
												</Box>
											</Paper>
										</Grid>

										<Grid item xs={12} md={4}>
											<Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: "action.hover" }}>
												<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
													<Typography variant="h6" sx={{ fontWeight: 700 }} color="primary.main">
														{launchSummary.totalFormatted}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														Total lançado
													</Typography>
												</Box>
											</Paper>
										</Grid>
									</Grid>
								</Paper>
							</Grid>
						</Grid>
					</VForm>
				</Box>

				{/* Footer fixo */}
				<Box
					sx={{
						px: 3,
						py: 2,
						borderTop: "1px solid",
						borderColor: "divider",
						flexShrink: 0,
						display: "flex",
						justifyContent: "center",
						gap: 2,
					}}
				>
					<Button color="inherit" disableElevation variant={"contained"} startIcon={<Icon>arrow_back_Ico</Icon>} onClick={() => onClose(false)}>
						{"Voltar"}
					</Button>
					<Button
						color="primary"
						disableElevation
						variant={"contained"}
						onClick={() => formRef.current?.submitForm()}
						disabled={isUploading || isLoading || isDuplicatingAttachments}
					>
						{"Confirmar"}
					</Button>
				</Box>
			</Box>
		</Modal>

			<ModalPreviewValue
				open={Boolean(previewAttachment)}
				onClose={handleCloseAttachmentPreview}
				previewUrl={previewUrl}
				previewDoc={{ mimetype: previewAttachment?.file.type }}
				isLoading={false}
			/>
		</>
	);
};
